import React, { useMemo } from "react";
import toast from "react-hot-toast";
import { PageList, ENUM_FORM_DISPLAY } from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { IconPencil, IconTrash } from "@tabler/icons";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { mcaProcessColumns } from "../../components/mca/mcaTableHelpers";
import { buildMcaProcessTableSchema } from "../../helpers/mcaProcessTableSchema";

const apiEntity = "mcaProcess";

const formSchema = {
  title: "Approval Process",
  apiEntity,
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "sm",
  },
  initialValues: { name: "" },
  layout: {
    fields: [],
  },
};

const McaProcess = () => {
  const [api] = useServerApi();
  const tabNav = useTabNavigate();

  const listActions = useMemo(
    () => [
      {
        label: "New Process",
        onClick: () =>
          tabNav.open({
            pathname: "/management/mcaProcess/new",
            label: "New Approval Process",
          }),
        requiresWrite: true,
      },
    ],
    [tabNav]
  );

  const tableSchema = useMemo(() => {
    const base = buildMcaProcessTableSchema();
    return {
      ...base,
      columns: () => [
        ...mcaProcessColumns(),
        {
          cellRender: ActionCellRender,
          isAction: true,
          actions: [
            { name: "edit", label: "Edit", icon: IconPencil },
          ],
          actionMenu: [
            {
              name: "delete",
              label: "Delete",
              icon: IconTrash,
              props: { color: "red" },
            },
          ],
        },
      ],
      onActionBtnClickCustom: async ({ action, data, fetchData }) => {
        if (action === "edit") {
          tabNav.open({
            pathname: `/management/mcaProcess/${data._id}`,
            label: data.name
              ? `MCA - ${data.name}`
              : data.code
                ? `MCA - ${data.code}`
                : "MCA - Approval Process",
          });
          return;
        }
        if (action === "toggle") {
          if (data.status === "DFT") {
            toast.error("Draft processes must be submitted for approval");
            return;
          }
          if (data.mcaPending) {
            toast.error("This process is under approval");
            return;
          }
          const res = await api.Mca.toggleStatus(data._id);
          if (res?.success) {
            toast.success(
              data.status === "ACTIVE" ? "Process deactivated" : "Process activated"
            );
            fetchData?.();
          }
          return;
        }
        if (action === "delete") {
          if (!window.confirm("Are you sure you want to delete this approval process?")) {
            return;
          }
          const res = await api.removeById({ apiEntity, id: data._id });
          if (res?.success) {
            toast.success("Process deleted");
            fetchData?.();
          } else {
            const message =
              res?.error?.message ||
              res?.message ||
              api.getErrorMsg(res?.error) ||
              "Delete failed";
            toast.error(message);
          }
        }
      },
    };
  }, [api, tabNav]);

  return (
    <PageList
      apiEntity={apiEntity}
      title="Approval Process"
      formSchema={formSchema}
      showAddButton={false}
      customActions={listActions}
      tableSchema={tableSchema}
      columnFilterLayout="toolbar"
      initSort={{ by: "createdAt", order: "desc" }}
    />
  );
};

export default McaProcess;
